<script setup>
  import { ref, onMounted, watch, nextTick } from 'vue';
  import { useI18n } from 'vue-i18n';
  import axios from '../../services/ApiService.js';
  import Pagination from '../Pagination.vue';

  const { t } = useI18n();
  const i18nScope = ref('components.imprints_list');

  const emit = defineEmits(['view-authors']);

  const props = defineProps({
    searchQuery: {
      type: String,
      default: ''
    },
    selectedAudience: {
      type: String,
      default: ''
    },
    selectedTerritory: {
      type: String,
      default: ''
    },
    selectedLanguage: {
      type: String,
      default: ''
    },
    showOnlyFollowed: {
      type: Boolean,
      default: false
    }
  });

  const imprintsLoading = ref(false);
  const imprints = ref([]);
  const currentPage = ref(1);
  const itemsPerPage = ref(20);
  const maxPagesToShow = ref(12);
  const totalItems = ref(0);
  const sortBy = ref('name');
  const sortOrder = ref('asc');
  const previousSortBy = ref('name');
  const previousSortOrder = ref('asc');
  const followingInProgress = ref(null);
  const toastMessage = ref('');
  const toastMessageType = ref('success');

  const fetchImprints = async () => {
    imprintsLoading.value = true;

    try {
      let url = `/api/imprints/list.json?page=${currentPage.value}&per_page=${itemsPerPage.value}`;
      
      // Add search query if present
      if (props.searchQuery) url += `&search=${encodeURIComponent(props.searchQuery)}`;
      
      // Add audience filter if present
      if (props.selectedAudience) url += `&audience=${encodeURIComponent(props.selectedAudience)}`;
      
      // Add territory filter if present
      if (props.selectedTerritory) url += `&territory=${encodeURIComponent(props.selectedTerritory)}`;
      
      // Add language filter if present
      if (props.selectedLanguage) url += `&language=${encodeURIComponent(props.selectedLanguage)}`;
      
      // Add followed filter if present
      if (props.showOnlyFollowed) url += `&followed=true`;
      
      // Add sorting
      url += `&sort_by=${sortBy.value}&sort_order=${sortOrder.value}`;
      
      const response = await axios.get(url);
      
      imprints.value = response.data.imprints || [];
      totalItems.value = response.data.total || 0;

    } catch (error) {
      console.error('Error fetching imprints:', error);
      imprints.value = [];
      totalItems.value = 0;
    } finally {
      imprintsLoading.value = false;
    }
  };

  const clearImprints = () => {
    imprints.value = [];
    currentPage.value = 1;
  };

  const handleSort = (column) => {
    if (sortBy.value === column) {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
      sortBy.value = column;
      // Default to desc for popularity and bestsellers
      sortOrder.value = (column === 'popularity' || column === 'bestsellers') ? 'desc' : 'asc';
    }
    
    // Reset to page 1 if sorting changed
    if (sortBy.value !== previousSortBy.value || sortOrder.value !== previousSortOrder.value) {
      currentPage.value = 1;
    }
    
    // Update previous values
    previousSortBy.value = sortBy.value;
    previousSortOrder.value = sortOrder.value;
    
    fetchImprints();
  };

  const getSortIcon = (column) => {
    if (sortBy.value !== column) return '';
    return sortOrder.value === 'asc' ? '↑' : '↓';
  };

  const formatLoanCount = (count) => {
    if (!count || count === 0) return '-';
    return count.toLocaleString();
  };

  const handlePageChange = (page) => {
    currentPage.value = page;
    fetchImprints();
  };

  const showToast = (message, type = 'success') => {
    toastMessage.value = message;
    toastMessageType.value = type;
    const toastEl = document.getElementById('imprintFollowToast');
    const toast = new bootstrap.Toast(toastEl);
    toast.show();
  };

  const toggleFollowing = async (imprint) => {
    if (followingInProgress.value === imprint.id) return;
    
    try {
      followingInProgress.value = imprint.id;
      const isCurrentlyFollowed = imprint.is_followed;
      const url = `/api/imprints/${imprint.id}/${isCurrentlyFollowed ? 'unfollow' : 'follow'}.json`;
      const method = isCurrentlyFollowed ? 'delete' : 'post';
      
      const response = await axios[method](url);
      
      if (response.data.success) {
        imprint.is_followed = response.data.is_followed;
        
        const message = response.data.is_followed 
          ? t(`${i18nScope.value}.toast.now_following`, { name: imprint.name })
          : t(`${i18nScope.value}.toast.unfollowed`, { name: imprint.name });
        showToast(message, 'success');
      }
    } catch (error) {
      console.error('Error toggling following:', error);
      showToast(t(`${i18nScope.value}.toast.error`), 'error');
    } finally {
      followingInProgress.value = null;
    }
  };

  const handleViewPublications = (imprint) => {
    window.open(`/recent?imprint_ids=${imprint.id}&languages=all`, '_blank');
  };

  const handleViewAuthors = (imprint) => {
    emit('view-authors', imprint);
  };

  defineExpose({
    fetchImprints,
    clearImprints
  });

  onMounted(() => {
    fetchImprints();
  });
</script>

<template>
  <div class="imprints-table">
    <div :class="['align-items-center border-0', toastMessageType === 'error' ? 'toast_error toast' : 'toast']" role="alert" aria-live="assertive" aria-atomic="true" id="imprintFollowToast">
      <div class="d-flex">
        <div class="toast-body">
          {{ toastMessage }}
        </div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div v-if="imprintsLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ t(`${i18nScope}.loading`) }}</span>
      </div>
    </div>

    <!-- Table -->
    <div v-else-if="imprints.length > 0" class="table-responsive">
      <table class="table table-hover table-striped">
        <thead class="table-light">
          <tr>
            <th scope="col" class="sortable" @click="handleSort('name')">
              {{ t(`${i18nScope}.table.name`) }} {{ getSortIcon('name') }}
            </th>
            <th scope="col" class="sortable" @click="handleSort('territory')">
              {{ t(`${i18nScope}.table.territory`) }} {{ getSortIcon('territory') }}
            </th>
            <th scope="col" class="sortable" @click="handleSort('state')">
              {{ t(`${i18nScope}.table.state`) }} {{ getSortIcon('state') }}
            </th>
            <th scope="col" class="sortable" @click="handleSort('popularity')">
              {{ t(`${i18nScope}.table.popularity_in_libraries`) }} {{ getSortIcon('popularity') }}
            </th>
            <th scope="col" class="sortable text-end" @click="handleSort('bestsellers')">
              {{ t(`${i18nScope}.table.bestsellers`) }} {{ getSortIcon('bestsellers') }}
            </th>
            <th scope="col">
              {{ t(`${i18nScope}.table.publishing_groups`) }}
            </th>
            <th scope="col" class="text-center">
              {{ t(`${i18nScope}.table.actions`) }}
            </th>
            <th scope="col" class="text-center">
              {{ t(`${i18nScope}.table.following`) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="imprint in imprints" :key="imprint.id">
            <td>{{ imprint.name }}</td>
            <td>{{ imprint.territory || '-' }}</td>
            <td>{{ imprint.state || '-' }}</td>
            <td class="text-end">
              {{ formatLoanCount(imprint.loan_count) }}
            </td>
            <td class="text-end">
              {{ formatLoanCount(imprint.bestseller_count) }}
            </td>
            <td>
              <span v-if="imprint.publishing_groups && imprint.publishing_groups.length > 0">
                {{ imprint.publishing_groups.join(', ') }}
              </span>
              <span v-else>-</span>
            </td>
            <td class="text-center">
              <i 
                class="bi bi-journals action-icon me-3"
                @click="handleViewPublications(imprint)"
                data-bs-toggle="tooltip" :data-bs-title="t(`${i18nScope}.table.publications`)" :aria-label="t(`${i18nScope}.table.publications`)"
              ></i>
              <i 
                class="bi bi-people-fill action-icon"
                @click="handleViewAuthors(imprint)"
                data-bs-toggle="tooltip" :data-bs-title="t(`${i18nScope}.table.authors`)" :aria-label="t(`${i18nScope}.table.authors`)"
              ></i>
            </td>
            <td class="text-center">
              <i 
                :class="imprint.is_followed ? 'bi bi-bookmark-plus-fill action-icon' : 'bi bi-bookmark-plus action-icon'"
                @click="toggleFollowing(imprint)"
                data-bs-toggle="tooltip" :data-bs-title="imprint.is_followed ? t(`${i18nScope}.table.unfollow`) : t(`${i18nScope}.table.follow`)" :aria-label="imprint.is_followed ? t(`${i18nScope}.table.unfollow`) : t(`${i18nScope}.table.follow`)"
              ></i>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <Pagination
        :current-page="currentPage"
        :total-items="totalItems"
        :items-per-page="itemsPerPage"
        :max-pages-to-show="maxPagesToShow"
        @page-change="handlePageChange"
      />
    </div>

    <!-- No Results -->
    <div v-else class="text-center py-5">
      <p class="text-muted">{{ t(`${i18nScope}.no_results`) }}</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .imprints-table {
    position: relative;

    .sortable {
      cursor: pointer;
      user-select: none;
      
      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }
    }

  .action-icon {
    cursor: pointer;
    font-size: 1.2rem;
    color: var(--primary);

    &:hover {
      opacity: 0.8;
    }
  }

    .table {
      margin-bottom: 20px;
    }
  }

  .toast {
    background-color: var(--primary);
    color: white;
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 1050;
    min-width: 300px;
  }
  .toast_error {
    background-color: var(--danger);
  }
</style>
